/**
 * Module #34 Phase A chunk 7 — proactive surveillance insights hook.
 *
 * Wraps GET /v1/copilot/insights + POST /v1/copilot/insights/:id/dismiss. Backs
 * the floating copilot bubble: the badge count, the insight popover, and the
 * proactive pop-up for high/critical cards.
 *
 * Polls on mount, on a slow interval (insights turn over on a daily cadence — no
 * need to hammer), and on window focus so a returning user sees a fresh brief.
 * When the Module #8 notification realtime channel drives bell pings for
 * high-priority insights (chunk 7e), that can call refresh() for an instant
 * update instead of waiting for the poll.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSession } from './useSession';
import { fetchWithAuth } from '../lib/accessToken';

export type InsightPriority = 'critical' | 'high' | 'normal' | 'low';

export interface CopilotSuggestedAction {
  toolName: string;
  input: Record<string, unknown>;
  label?: string;
  tier?: string;
}

/** Panel grouping slug from the IE (accounts_receivable | customer_messages | tasks | opportunities). */
export type InsightCategory =
  | 'accounts_receivable'
  | 'customer_messages'
  | 'tasks'
  | 'opportunities';

export interface CopilotInsight {
  insightId: number;
  cadence: string;
  priority: InsightPriority;
  insightKind: string;
  category: InsightCategory;
  narrative: string;
  suggestedActionCalls: CopilotSuggestedAction[];
  createdAt: string;
  expiresAt: string | null;
}

export interface CopilotInsightCounts {
  total: number;
  highOrCritical: number;
}

export type InsightsStatus =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'error'; detail: string };

/** Outcome of a one-click direct-fire of a card's pre-staged action. */
export interface FireActionResult {
  ok: boolean;
  /** A short human message: 'Sent', 'Done', or the failure reason. */
  message: string;
}

export interface UseCopilotInsightsReturn {
  insights: CopilotInsight[];
  counts: CopilotInsightCounts;
  status: InsightsStatus;
  refresh: (opts?: { silent?: boolean }) => Promise<void>;
  dismiss: (id: number) => Promise<boolean>;
  /** Fire a card's pre-staged action directly (no chat round-trip). */
  fireAction: (
    id: number,
    actionIndex: number,
    confirmationText?: string,
  ) => Promise<FireActionResult>;
}

/** Turn a tool's structured result into a one-line status for the card. */
function summarizeFireResult(result: unknown): string {
  if (typeof result === 'object' && result !== null) {
    const r = result as Record<string, unknown>;
    if (r['sent'] === true) return 'Sent';
    if (r['sent'] === false && typeof r['reason'] === 'string') return `Not sent: ${r['reason']}`;
    if (typeof r['message'] === 'string' && r['message'].length > 0) return r['message'];
  }
  return 'Done';
}

interface InsightApiItem {
  insight_id: number;
  cadence: string;
  priority: InsightPriority;
  insight_kind: string;
  category: InsightCategory | null;
  narrative: string;
  suggested_action_calls: CopilotSuggestedAction[] | null;
  created_at: string;
  expires_at: string | null;
}

interface InsightsApiResponse {
  insights: InsightApiItem[];
  counts: { total: number; high_or_critical: number };
}

const POLL_MS = 60_000;

function ieBaseUrl(): string | null {
  const url = import.meta.env.VITE_INTELLIGENCE_BASE_URL as string | undefined;
  return url && url.length > 0 ? url.replace(/\/$/, '') : null;
}

export function useCopilotInsights(): UseCopilotInsightsReturn {
  const { userId } = useSession();
  const base = useMemo(() => ieBaseUrl(), []);
  const [insights, setInsights] = useState<CopilotInsight[]>([]);
  const [counts, setCounts] = useState<CopilotInsightCounts>({ total: 0, highOrCritical: 0 });
  const [status, setStatus] = useState<InsightsStatus>({ kind: 'idle' });
  // Avoid overlapping fetches (poll + focus + manual could otherwise stack).
  const inFlight = useRef(false);

  const refresh = useCallback(
    async (opts?: { silent?: boolean }): Promise<void> => {
      if (base === null || userId === null) {
        setStatus({ kind: 'idle' });
        setInsights([]);
        setCounts({ total: 0, highOrCritical: 0 });
        return;
      }
      if (inFlight.current) return;
      inFlight.current = true;
      if (opts?.silent !== true) setStatus({ kind: 'loading' });
      try {
        const res = await fetchWithAuth(`${base}/v1/copilot/insights?limit=50`);
        if (!res.ok) {
          const body = (await res.json().catch(() => null)) as { detail?: string } | null;
          setStatus({ kind: 'error', detail: body?.detail ?? `HTTP ${res.status}` });
          return;
        }
        const data = (await res.json()) as InsightsApiResponse;
        setInsights(
          data.insights.map((i) => ({
            insightId: i.insight_id,
            cadence: i.cadence,
            priority: i.priority,
            insightKind: i.insight_kind,
            // Fall back to 'tasks' if an older IE build omits category.
            category: i.category ?? 'tasks',
            narrative: i.narrative,
            suggestedActionCalls: i.suggested_action_calls ?? [],
            createdAt: i.created_at,
            expiresAt: i.expires_at,
          })),
        );
        setCounts({ total: data.counts.total, highOrCritical: data.counts.high_or_critical });
        setStatus({ kind: 'idle' });
      } catch (err) {
        setStatus({ kind: 'error', detail: err instanceof Error ? err.message : 'Network error' });
      } finally {
        inFlight.current = false;
      }
    },
    [base, userId],
  );

  // Load on mount + when session/base flips.
  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Slow poll + refresh on window focus (silent — no loading flash).
  useEffect(() => {
    if (base === null || userId === null) return;
    const interval = window.setInterval(() => void refresh({ silent: true }), POLL_MS);
    const onFocus = (): void => void refresh({ silent: true });
    window.addEventListener('focus', onFocus);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener('focus', onFocus);
    };
  }, [base, userId, refresh]);

  const dismiss = useCallback(
    async (id: number): Promise<boolean> => {
      if (base === null || userId === null) return false;
      let res: Response;
      try {
        res = await fetchWithAuth(`${base}/v1/copilot/insights/${id}/dismiss`, {
          method: 'POST',
        });
      } catch {
        return false;
      }
      if (!res.ok) return false;
      // Optimistic: drop it locally + recompute counts.
      setInsights((prev) => {
        const next = prev.filter((i) => i.insightId !== id);
        setCounts({
          total: next.length,
          highOrCritical: next.filter((i) => i.priority === 'critical' || i.priority === 'high')
            .length,
        });
        return next;
      });
      return true;
    },
    [base, userId],
  );

  const fireAction = useCallback(
    async (
      id: number,
      actionIndex: number,
      confirmationText?: string,
    ): Promise<FireActionResult> => {
      if (base === null || userId === null) {
        return { ok: false, message: 'Copilot is not configured' };
      }
      let res: Response;
      try {
        res = await fetchWithAuth(`${base}/v1/copilot/insights/${id}/fire`, {
          method: 'POST',
          headers: {
            'content-type': 'application/json',
          },
          body: JSON.stringify({
            action_index: actionIndex,
            ...(confirmationText !== undefined && { confirmation_text: confirmationText }),
          }),
        });
      } catch (err) {
        return { ok: false, message: err instanceof Error ? err.message : 'Network error' };
      }
      const body = (await res.json().catch(() => null)) as {
        fired?: boolean;
        result?: unknown;
        detail?: string;
      } | null;
      if (!res.ok || body?.fired !== true) {
        return { ok: false, message: body?.detail ?? `Couldn't complete (HTTP ${res.status})` };
      }
      // The card flipped to in_progress server-side; refresh so the brief reflects it.
      void refresh({ silent: true });
      return { ok: true, message: summarizeFireResult(body.result) };
    },
    [base, userId, refresh],
  );

  return { insights, counts, status, refresh, dismiss, fireAction };
}
